import { DateOnly } from '@if/core/date'
import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  FieldGrid,
  useFormDialog,
} from '@if/mui'
import { getToday } from '@src/services/DateTimeProvider'
import { useTranslatedFeeTypeList } from '@src/types/constants'
import { ExistingFee, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { Pencil } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EditFeeSchemaType, getEditFeeSchema } from './EditFee-schema'

interface UseEditFeeDialogProps {
  /** The earliest date a fee can be moved to; the latest is today. */
  activationDate: DateOnly
  isLockedByOther: boolean
  /** Recomputes the calendar with the edit; the dialog shows its loading state until it settles, and stays open on error. */
  onSubmit: (values: EditFeeSchemaType, fee: ExistingFee) => Promise<SignedCalendarSimulation>
}

/**
 * Edits one fee, completed ones included, on the same form as the add dialog, and resolves with the
 * calendar recomputed with the edit.
 */
export const useEditFeeDialog = ({ activationDate, isLockedByOther, onSubmit }: UseEditFeeDialogProps) => {
  const { t } = useTranslation()
  const feeTypeOptions = useTranslatedFeeTypeList()
  const schema = useMemo(() => getEditFeeSchema(activationDate, getToday()), [activationDate])

  return useFormDialog<EditFeeSchemaType, ExistingFee, SignedCalendarSimulation>({
    icon: Pencil,
    title: t('loan.feesDialog.editFee'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    defaultValues: (fee) => ({
      feeType: fee.feeType,
      amount: fee.amount,
      effectiveDate: fee.effectiveDate,
      note: fee.note,
    }),
    successMessage: false,
    onSubmit: (values, { props: fee }) => onSubmit(values, fee),
    confirmDisabled: () => isLockedByOther,
    confirmTooltip: () => (isLockedByOther ? t('common.lockTooltip.lockedByOther') : null),
    content: ({ form }) => (
      <FieldGrid columns={{ xs: 1, sm: 2 }}>
        <ControlledSelect
          control={form.control}
          name="feeType"
          options={feeTypeOptions}
          label={t('loan.feesDialog.feeType')}
          disableEmptyOption
          disabled
        />
        <ControlledNumberField
          control={form.control}
          name="amount"
          isMoney
          label={t('loan.feesDialog.amount')}
          disabled={isLockedByOther}
        />
        <ControlledDateOnlyPicker
          control={form.control}
          name="effectiveDate"
          label={t('loan.feesDialog.effectiveDate')}
          minDate={activationDate}
          maxDate={getToday()}
          disabled={isLockedByOther}
        />
        <ControlledTextField
          control={form.control}
          name="note"
          label={t('loan.feesDialog.note')}
          span="full"
          disabled={isLockedByOther}
        />
      </FieldGrid>
    ),
  })
}
